import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersApi, addressesApi, cartApi } from '../api';
import { usePlatform } from '../hooks/usePlatform';
import { useAppAlert } from '../contexts/AlertContext';
import { Loading } from '../components/Loading';
import { CustomerContext } from '../contexts/CustomerContext';
import { pluralize } from '../utils/pluralize';
import type { Address } from '../types';
import './CheckoutPage.css';

type Step = 1 | 2 | 3;

export const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();
  const platform = usePlatform();
  const { showAlert } = useAppAlert();
  const customer = useContext(CustomerContext);

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Step 1: Personal Info
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2: Delivery Address
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);
  const [showNewAddressForm, setShowNewAddressForm] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street: '',
    house: '',
    apartment: '',
    city: '',
    postal_code: '',
    notes: '',
    is_default: false,
  });

  // Step 3: Payment & Notes
  const [paymentMethod] = useState('transfer'); // Пока только один метод
  const [orderNotes, setOrderNotes] = useState('');

   
  useEffect(() => {
    platform.showBackButton(() => navigate('/'));

    return () => {
      platform.hideBackButton();
    };
    // platform identity is stable; navigate is referentially stable in react-router v6
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Check if cart is empty
      const cartData = await cartApi.getCart();

      // Show notification if items were removed
      if (cartData.removed_items_count > 0) {
        const itemWord = pluralize(cartData.removed_items_count, 'товар', 'товара', 'товаров');
        showAlert(
          `${cartData.removed_items_count} ${itemWord} больше не доступен и был удален из корзины`
        );
      }

      if (!cartData.cart.items || cartData.cart.items.length === 0) {
        navigate('/', { replace: true });
        return;
      }

      // Load customer data for step 1
      if (customer) {
        setFirstName(customer.first_name || '');
        setLastName(customer.last_name || '');
        setPhone(customer.phone || '');
      }

      // Load addresses for step 2
      const addressesResponse = await addressesApi.getAddresses();
      const addressesData = addressesResponse?.addresses || [];
      setAddresses(addressesData);

      if (addressesData.length === 0) {
        // No addresses - show form to add new one
        setShowNewAddressForm(true);
      } else {
        // Find default address
        const defaultAddress = addressesData.find((addr) => addr.is_default);
        if (defaultAddress) {
          setSelectedAddressId(defaultAddress.id);
        } else if (addressesData.length > 0) {
          setSelectedAddressId(addressesData[0].id);
        }
      }
    } catch (err: unknown) {
      showAlert(err instanceof Error ? err.message : 'Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handlePersonalInfoNext = async () => {
    // Validate
    if (!firstName.trim()) {
      showAlert('Пожалуйста, укажите имя');
      return;
    }

    if (!phone.trim()) {
      showAlert('Пожалуйста, укажите телефон');
      return;
    }

    // Check if data changed
    const hasChanges =
      firstName !== (customer?.first_name || '') ||
      lastName !== (customer?.last_name || '') ||
      phone !== (customer?.phone || '');

    if (hasChanges) {
      try {
        setLoading(true);
        await customersApi.updateMe({
          first_name: firstName,
          last_name: lastName || undefined,
          phone: phone,
        });
      } catch (err: unknown) {
        showAlert(err instanceof Error ? err.message : 'Ошибка обновления данных');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    }

    setCurrentStep(2);
  };

  const handleAddressNext = async () => {
    if (showNewAddressForm) {
      // Validate new address
      if (!newAddress.street.trim() || !newAddress.house.trim()) {
        showAlert('Пожалуйста, укажите улицу и дом');
        return;
      }
      if (!newAddress.city.trim()) {
        showAlert('Пожалуйста, укажите город');
        return;
      }

      // Create address_line from components
      const addressParts = [
        `ул. ${newAddress.street}`,
        `д. ${newAddress.house}`,
      ];
      if (newAddress.apartment.trim()) {
        addressParts.push(`кв. ${newAddress.apartment}`);
      }
      const address_line = addressParts.join(', ');

      // Create new address
      try {
        setLoading(true);
        const createdAddress = await addressesApi.createAddress({
          address_line,
          city: newAddress.city,
          postal_code: newAddress.postal_code,
          notes: newAddress.notes.trim() || undefined,
          is_default: newAddress.is_default,
        });
        setSelectedAddressId(createdAddress.id);
        setAddresses([...addresses, createdAddress]);
        setShowNewAddressForm(false);
      } catch (err: unknown) {
        showAlert(err instanceof Error ? err.message : 'Ошибка создания адреса');
        setLoading(false);
        return;
      } finally {
        setLoading(false);
      }
    } else {
      // Check if address selected
      if (!selectedAddressId) {
        showAlert('Пожалуйста, выберите адрес доставки');
        return;
      }
    }

    setCurrentStep(3);
  };

  const handleCheckout = async () => {
    if (!selectedAddressId) {
      showAlert('Ошибка: адрес доставки не выбран');
      return;
    }

    try {
      setSubmitting(true);
      const checkoutData = await cartApi.checkout(
        selectedAddressId,
        paymentMethod,
        orderNotes.trim() || undefined
      );

      // Show notification if items were removed before checkout
      if (checkoutData.removed_items_count > 0) {
        const itemWord = pluralize(checkoutData.removed_items_count, 'товар', 'товара', 'товаров');
        showAlert(
          `${checkoutData.removed_items_count} ${itemWord} больше не доступен и был удален из заказа`,
          () => {
            // Navigate after user closes the notification
            navigate(`/checkout/success?orderId=${checkoutData.order.id}`, { replace: true });
          }
        );
      } else {
        // Navigate to success page with order ID
        navigate(`/checkout/success?orderId=${checkoutData.order.id}`, { replace: true });
      }
    } catch (err: unknown) {
      // Navigate to failure page
      navigate('/checkout/failure', { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && currentStep === 1) {
    return <Loading />;
  }

  return (
    <div className="checkout-page">
      <h1 className="checkout-title">Оформление заказа</h1>

      {/* Progress Steps */}
      <div className="checkout-steps">
        <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-number">1</div>
          <div className="step-label">Личные данные</div>
        </div>
        <div className="step-divider"></div>
        <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-number">2</div>
          <div className="step-label">Адрес</div>
        </div>
        <div className="step-divider"></div>
        <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-number">3</div>
          <div className="step-label">Оплата</div>
        </div>
      </div>

      {/* Step 1: Personal Info */}
      {currentStep === 1 && (
        <div className="checkout-step-content">
          <div className="step-section">
            <h2>Личная информация</h2>
            <p className="step-description">
              Проверьте и при необходимости обновите ваши данные
            </p>

            <div className="form-field">
              <label htmlFor="firstName">Имя *</label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Введите имя"
              />
            </div>

            <div className="form-field">
              <label htmlFor="lastName">Фамилия</label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Введите фамилию"
              />
            </div>

            <div className="form-field">
              <label htmlFor="phone">Телефон *</label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+7 999 123 45 67"
              />
            </div>

            <button
              className="checkout-button primary"
              onClick={handlePersonalInfoNext}
              disabled={loading}
            >
              {loading ? 'Сохранение...' : 'Перейти к адресу'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Delivery Address */}
      {currentStep === 2 && (
        <div className="checkout-step-content">
          <div className="step-section">
            <h2>Адрес доставки</h2>

            {!showNewAddressForm ? (
              <>
                {addresses.length === 0 ? (
                  <div className="no-addresses-message">
                    <p>У вас пока нет сохраненных адресов.</p>
                    <p>Добавьте адрес доставки для оформления заказа.</p>
                  </div>
                ) : (
                  <div className="addresses-list">
                    {addresses.map((address) => (
                      <div
                        key={address.id}
                        className={`address-card ${selectedAddressId === address.id ? 'selected' : ''}`}
                        onClick={() => setSelectedAddressId(address.id)}
                      >
                        <div className="address-header">
                          <div className="address-name">{address.address_line}</div>
                          {address.is_default && <span className="default-badge">По умолчанию</span>}
                        </div>
                        <div className="address-details">
                          {address.city}, {address.postal_code}
                        </div>
                        {selectedAddressId === address.id && (
                          <div className="selected-indicator">✓</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <button
                  className="add-address-button"
                  onClick={() => setShowNewAddressForm(true)}
                >
                  + Добавить новый адрес
                </button>

                {addresses.length > 0 && (
                  <div className="step-actions">
                    <button
                      className="checkout-button secondary"
                      onClick={() => setCurrentStep(1)}
                    >
                      Назад
                    </button>
                    <button
                      className="checkout-button primary"
                      onClick={handleAddressNext}
                      disabled={!selectedAddressId}
                    >
                      К оплате
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="step-description">Заполните данные нового адреса</p>

                <div className="form-field">
                  <label htmlFor="city">Город *</label>
                  <input
                    id="city"
                    type="text"
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                    placeholder="Москва"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="street">Улица *</label>
                  <input
                    id="street"
                    type="text"
                    value={newAddress.street}
                    onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })}
                    placeholder="Ленина"
                  />
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="house">Дом *</label>
                    <input
                      id="house"
                      type="text"
                      value={newAddress.house}
                      onChange={(e) => setNewAddress({ ...newAddress, house: e.target.value })}
                      placeholder="10"
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="apartment">Квартира</label>
                    <input
                      id="apartment"
                      type="text"
                      value={newAddress.apartment}
                      onChange={(e) => setNewAddress({ ...newAddress, apartment: e.target.value })}
                      placeholder="25"
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="postal_code">Почтовый индекс</label>
                  <input
                    id="postal_code"
                    type="text"
                    value={newAddress.postal_code}
                    onChange={(e) => setNewAddress({ ...newAddress, postal_code: e.target.value })}
                    placeholder="123456"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="addressNotes">Комментарий к адресу</label>
                  <textarea
                    id="addressNotes"
                    value={newAddress.notes}
                    onChange={(e) => setNewAddress({ ...newAddress, notes: e.target.value })}
                    placeholder="Не звонить в домофон"
                    rows={2}
                  />
                </div>

                <div className="form-field checkbox-field">
                  <label htmlFor="is_default" className="checkbox-label">
                    <input
                      id="is_default"
                      type="checkbox"
                      checked={newAddress.is_default}
                      onChange={(e) => setNewAddress({ ...newAddress, is_default: e.target.checked })}
                    />
                    <span>Сделать адресом по умолчанию</span>
                  </label>
                </div>

                <div className="step-actions">
                  <button
                    className="checkout-button secondary"
                    onClick={() => setShowNewAddressForm(false)}
                  >
                    Отмена
                  </button>
                  <button
                    className="checkout-button primary"
                    onClick={handleAddressNext}
                    disabled={loading}
                  >
                    {loading ? 'Сохранение...' : 'Продолжить'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Step 3: Payment & Notes */}
      {currentStep === 3 && (
        <div className="checkout-step-content">
          <div className="step-section">
            <h2>Способ оплаты</h2>

            <div className="payment-method-card selected">
              <div className="payment-icon">💳</div>
              <div className="payment-info">
                <div className="payment-name">Оплата у менеджера</div>
                <div className="payment-description">
                  С вами свяжется менеджер для подтверждения заказа и оплаты
                </div>
              </div>
              <div className="selected-indicator">✓</div>
            </div>

            <div className="form-field">
              <label htmlFor="orderNotes">Комментарий к заказу</label>
              <textarea
                id="orderNotes"
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                placeholder="Ваши пожелания"
                rows={3}
              />
            </div>

            <div className="step-actions">
              <button
                className="checkout-button secondary"
                onClick={() => setCurrentStep(2)}
              >
                Назад
              </button>
              <button
                className="checkout-button primary submit"
                onClick={handleCheckout}
                disabled={submitting}
              >
                {submitting ? 'Оформление...' : 'Оформить заказ'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
